import { Quote as QuoteIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { ZoomIn, ZoomOut, Hourglass, Rows3, Columns3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SafeImg } from "@/components/ImageGallery";
import { evDate } from "@/lib/eventDate";
import { makeTicks } from "@/lib/calFormat";

const BASE_W = 1100;

function JumpArcs({ placed, jumps }) {
  const pos = Object.fromEntries(placed.map((p) => [p.id, { x: p.x + 20, y: 58 + p.lane * 64 }]));
  const arcs = jumps.filter((j) => pos[j.departure?.id] && pos[j.arrival?.id]);
  if (!arcs.length) return null;
  return (
    <svg className="absolute inset-0 pointer-events-none z-20 overflow-visible" width="100%" height="100%" data-testid="jump-arcs">
      <defs><marker id="lf-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto"><path d="M0,0 L10,5 L0,10 z" fill="#8B5CF6" /></marker></defs>
      {arcs.map((j) => {
        const a = pos[j.departure.id], b = pos[j.arrival.id];
        const lift = Math.min(50, 20 + Math.abs(b.x - a.x) / 8);
        return <path key={j.id} className="lf-jump-arc" d={`M${a.x},${a.y} Q${(a.x + b.x) / 2},${Math.min(a.y, b.y) - lift} ${b.x},${b.y}`} fill="none" stroke="#8B5CF6" strokeWidth="2" markerEnd="url(#lf-arrow)" data-testid={`jump-arc-${j.id}`} />;
      })}
    </svg>
  );
}
const CARD_W = 170;

const jumpsFor = (e, jumps, timelineId) => jumps.filter((j) => (j.departure?.id === e.id && j.arrival?.timeline_id !== timelineId) || (j.arrival?.id === e.id && j.departure?.timeline_id !== timelineId));

const JumpBadge = ({ j, e, onJumpMarker, className }) => {
  const other = j.departure?.id === e.id ? j.arrival : j.departure;
  return other && (
    <span role="button" tabIndex={0} title={`${j.traveller_name} ${j.departure?.id === e.id ? "→" : "←"} ${other.timeline_name}: ${other.title}`}
      onClick={(ev) => { ev.stopPropagation(); onJumpMarker?.(other); }} data-testid={`jump-marker-${j.id}`}
      className={`flex items-center gap-0.5 rounded-full border border-[#8B5CF6] bg-[#0B0D10] px-1.5 py-0.5 text-[9px] text-[#C4B5FD] hover:bg-[#8B5CF6]/20 ${className}`}>
      <Hourglass className="h-2.5 w-2.5" />{other.timeline_name.slice(0, 10)}
    </span>
  );
};

function VerticalTrack({ events, branchPoint, scrubT, onEventClick, jumps, timelineId, onJumpMarker, evQuotes }) {
  const sorted = [...events].sort((a, b) => a.timestamp_int - b.timestamp_int);
  return (
    <ol className="relative ml-4 border-l border-[#2D3442] py-4 pr-4" data-testid="timeline-vertical">
      {sorted.map((e, i) => {
        const showBranch = branchPoint != null && e.timestamp_int >= branchPoint && (i === 0 || sorted[i - 1].timestamp_int < branchPoint);
        const past = scrubT != null && e.timestamp_int <= scrubT;
        return (
          <li key={e.id} className="relative mb-3 pl-6">
            {showBranch && <div className="mb-3 -ml-6 border-t border-dashed border-[#8B5CF6] pl-6 pt-1 text-[10px] uppercase tracking-widest text-[#8B5CF6]" data-testid="vtrack-branch-point">divergence</div>}
            <span className="absolute -left-[5px] top-4 h-2.5 w-2.5 rounded-full" style={{ background: e.inherited ? "#A8A090" : past ? "#F59E0B" : "#D97706", boxShadow: past ? "0 0 8px #F59E0B" : "none" }} />
            <button onClick={() => onEventClick(e)} data-testid={`vtrack-event-${e.id}`}
              className={`w-full rounded border px-3 py-2 text-left transition-colors ${e.inherited ? "opacity-45 border-dashed border-[#2D3442] bg-[#13171F]" : "border-[#2D3442] bg-[#1A202C] hover:border-[#D97706]/60"}`}>
              <div className="flex items-center gap-2">
                {e.image_url && <SafeImg src={e.image_url} alt="" className="h-9 w-9 shrink-0 rounded-sm" testid={`vtrack-event-image-${e.id}`} />}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm text-[#F3EBDD]">{e.title}</div>
                  <div className="text-[11px] text-[#A8A090]">{evDate(e)}</div>
                </div>
              </div>
              {(evQuotes[e.id]?.length > 0 || jumpsFor(e, jumps, timelineId).length > 0) && (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {evQuotes[e.id]?.length > 0 && <span className="flex items-center gap-0.5 rounded-full border border-[#D97706]/70 px-1.5 py-0.5 text-[9px] text-[#F59E0B]"><QuoteIcon className="h-2.5 w-2.5" />{evQuotes[e.id].length}</span>}
                  {jumpsFor(e, jumps, timelineId).map((j) => <JumpBadge key={j.id} j={j} e={e} onJumpMarker={onJumpMarker} />)}
                </div>
              )}
            </button>
          </li>
        );
      })}
    </ol>
  );
}

function layout(events, min, span, width) {
  const lanes = [];
  return events.map((e) => {
    const x = ((e.timestamp_int - min) / span) * width;
    let lane = lanes.findIndex((end) => end < x - 8);
    if (lane === -1) { lane = lanes.length; lanes.push(0); }
    lanes[lane] = x + CARD_W;
    return { ...e, x, lane };
  });
}

export const TimelineTrack = ({ fmt, events, branchPoint, scrubT, onEventClick, jumps = [], timelineId, onJumpMarker, evQuotes = {} }) => {
  const [zoom, setZoom] = useState(1);
  const [vertical, setVertical] = useState(() => localStorage.getItem("lf-track-vertical") === "1");
  const flip = () => setVertical((v) => { localStorage.setItem("lf-track-vertical", v ? "0" : "1"); return !v; });
  const { min, span } = useMemo(() => {
    const ts = events.map((e) => e.timestamp_int).concat(branchPoint != null ? [branchPoint] : []);
    const lo = Math.min(...ts), hi = Math.max(...ts);
    const pad = Math.max(1, Math.round((hi - lo) * 0.08));
    return { min: lo - pad, span: hi - lo + pad * 2 };
  }, [events, branchPoint]);
  const width = BASE_W * zoom;
  const placed = layout(events, min, span, width);
  const lanes = Math.max(1, ...placed.map((p) => p.lane + 1));
  const height = 70 + lanes * 64;
  const ticks = makeTicks(fmt, min, span, Math.round(8 * zoom));
  const px = (t) => ((t - min) / span) * width;

  return (
    <div className="relative rounded-md border border-[#2D3442] bg-[#0F131A]" data-testid="timeline-track">
      <div className="absolute right-3 top-3 z-30 flex gap-1 rounded backdrop-blur-md bg-[#13171F]/80 border border-[#2D3442]">
        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={flip} title={vertical ? "Horizontal view" : "Vertical view"} aria-label={vertical ? "Horizontal view" : "Vertical view"} data-testid="track-orientation-toggle">
          {vertical ? <Columns3 className="h-4 w-4" /> : <Rows3 className="h-4 w-4" />}
        </Button>
        {!vertical && <>
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setZoom((z) => Math.max(0.5, z / 1.5))} data-testid="track-zoom-out"><ZoomOut className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setZoom((z) => Math.min(12, z * 1.5))} data-testid="track-zoom-in"><ZoomIn className="h-4 w-4" /></Button>
        </>}
      </div>
      {vertical ? <div className="pt-10"><VerticalTrack events={events} branchPoint={branchPoint} scrubT={scrubT} onEventClick={onEventClick} jumps={jumps} timelineId={timelineId} onJumpMarker={onJumpMarker} evQuotes={evQuotes} /></div> : <div className="overflow-x-auto lf-scroll" onWheel={(e) => { if (e.ctrlKey) { e.preventDefault(); setZoom((z) => Math.min(12, Math.max(0.5, z * (e.deltaY < 0 ? 1.15 : 0.87)))); } }}>
        <div className="relative mx-6" style={{ width: width + CARD_W, height }}>
          <div className="absolute left-0 right-0 top-9 h-px bg-[#2D3442]" style={{ width }} />
          {ticks.map(({ t, label }) => (
            <div key={t} className="absolute top-0 text-[10px] font-mono-lf text-[#A8A090]" style={{ left: px(t) }}>
              <div className={`whitespace-nowrap ${px(t) < 60 ? "" : "-translate-x-1/2"}`}>{label}</div><div className="mt-3 h-3 w-px bg-[#2D3442]" />
            </div>
          ))}
          {branchPoint != null && (
            <div className="absolute top-6 bottom-0 border-l border-dashed border-[#8B5CF6]" style={{ left: px(branchPoint) }} data-testid="track-branch-point">
              <span className="absolute -top-0 left-1.5 text-[10px] uppercase tracking-widest text-[#8B5CF6] whitespace-nowrap">divergence</span>
            </div>
          )}
          {scrubT != null && <div className="absolute top-6 bottom-0 w-0.5 bg-[#F59E0B] shadow-[0_0_12px_#F59E0B] z-10 pointer-events-none" style={{ left: px(scrubT) }} data-testid="track-scrub-line" />}
          <JumpArcs placed={placed} jumps={jumps} />
          {placed.map((e) => (
            <button key={e.id} onClick={() => onEventClick(e)} data-testid={`track-event-${e.id}`}
              className={`absolute text-left rounded border px-2.5 py-1.5 transition-[transform,border-color] duration-200 hover:-translate-y-0.5 ${e.inherited ? "opacity-45 border-dashed border-[#2D3442] bg-[#13171F]" : "border-[#2D3442] bg-[#1A202C] hover:border-[#D97706]/60"}`}
              style={{ left: e.x, top: 58 + e.lane * 64, width: CARD_W - 12 }}>
              <span className="absolute -left-[5px] top-3 h-2.5 w-2.5 rounded-full" style={{ background: e.inherited ? "#A8A090" : "#D97706" }} />
              {evQuotes[e.id]?.length > 0 && (
                <span title={`${evQuotes[e.id].length} quote${evQuotes[e.id].length > 1 ? "s" : ""} said here`} data-testid={`track-event-quotes-${e.id}`}
                  className="absolute -top-2.5 left-3 flex items-center gap-0.5 rounded-full border border-[#D97706]/70 bg-[#0B0D10] px-1.5 py-0.5 text-[9px] text-[#F59E0B]">
                  <QuoteIcon className="h-2.5 w-2.5" />{evQuotes[e.id].length}
                </span>
              )}
              <div className="flex items-center gap-2">
                {e.image_url && <SafeImg src={e.image_url} alt="" className="h-8 w-8 shrink-0 rounded-sm" testid={`track-event-image-${e.id}`} />}
                <div className="min-w-0">
                  <div className="text-sm text-[#F3EBDD] truncate">{e.title}</div>
                  <div className="text-[10px] text-[#A8A090] truncate">{evDate(e)}</div>
                </div>
              </div>
              {jumpsFor(e, jumps, timelineId).map((j) => <JumpBadge key={j.id} j={j} e={e} onJumpMarker={onJumpMarker} className="absolute -top-2.5 -right-2" />)}
            </button>
          ))}
        </div>
      </div>}
    </div>
  );
};
